/**
 * Proactive session refresh scheduler
 *
 * Automatically refreshes the auth session before the access token expires.
 * This prevents users from being logged out while actively using the app.
 *
 * The refresh is scheduled to run 2 minutes before token expiry.
 */

// Refresh 2 minutes before expiry to ensure smooth continuation
const REFRESH_BUFFER_MS = 2 * 60 * 1000

// Minimum time before scheduling a refresh (avoid immediate refresh loops)
const MIN_REFRESH_DELAY_MS = 30 * 1000

// Singleton state for the refresh timer
let refreshTimerId: ReturnType<typeof setTimeout> | null = null
let isRefreshing = false

export const useSessionRefresh = () => {
  const { sessionExpiresAt, refreshSession, isAuthenticated } = useAuth()

  const clearRefreshTimer = () => {
    if (refreshTimerId) {
      clearTimeout(refreshTimerId)
      refreshTimerId = null
    }
  }

  const scheduleRefresh = () => {
    // Clear any existing timer
    clearRefreshTimer()

    // Don't schedule if not authenticated or no expiry time
    if (!isAuthenticated.value || !sessionExpiresAt.value) {
      return
    }

    const now = Date.now()
    const expiresAt = sessionExpiresAt.value
    const timeUntilExpiry = expiresAt - now

    // If already expired or very close to expiring, refresh immediately
    if (timeUntilExpiry <= REFRESH_BUFFER_MS) {
      performRefresh()
      return
    }

    // Schedule refresh for REFRESH_BUFFER_MS before expiry
    const refreshDelay = Math.max(
      timeUntilExpiry - REFRESH_BUFFER_MS,
      MIN_REFRESH_DELAY_MS
    )

    refreshTimerId = setTimeout(() => {
      performRefresh()
    }, refreshDelay)

    if (import.meta.dev) {
      const refreshAt = new Date(now + refreshDelay)
      console.debug(
        `[SessionRefresh] Scheduled refresh in ${Math.round(refreshDelay / 1000)}s at ${refreshAt.toLocaleTimeString()}`
      )
    }
  }

  const performRefresh = async () => {
    // Prevent concurrent refresh attempts
    if (isRefreshing) return
    isRefreshing = true

    try {
      if (import.meta.dev) {
        console.debug('[SessionRefresh] Refreshing session...')
      }

      const success = await refreshSession()

      if (success) {
        if (import.meta.dev) {
          console.debug('[SessionRefresh] Session refreshed successfully')
        }
        // Schedule the next refresh
        scheduleRefresh()
      } else {
        if (import.meta.dev) {
          console.debug('[SessionRefresh] Refresh failed, user will be redirected on next navigation')
        }
      }
    } catch (error) {
      console.error('[SessionRefresh] Failed to refresh session:', error)
      // Don't reschedule on error - the auth middleware will handle redirect
    } finally {
      isRefreshing = false
    }
  }

  /**
   * Start the refresh scheduler.
   * Call this after successful login or session restoration.
   */
  const startRefreshScheduler = () => {
    scheduleRefresh()
  }

  /**
   * Stop the refresh scheduler.
   * Call this on logout.
   */
  const stopRefreshScheduler = () => {
    clearRefreshTimer()
  }

  // Auto-reschedule when sessionExpiresAt changes
  watch(sessionExpiresAt, (newExpiry) => {
    if (newExpiry && isAuthenticated.value) {
      scheduleRefresh()
    } else {
      clearRefreshTimer()
    }
  })

  // Clean up on component unmount (for apps using this in app.vue)
  onUnmounted(() => {
    // Don't clear the timer on unmount - it should persist across route changes
    // The timer is cleared explicitly on logout via stopRefreshScheduler
  })

  return {
    startRefreshScheduler,
    stopRefreshScheduler,
    scheduleRefresh
  }
}
